import { createApp, inject } from 'vue';
import App from './App.vue';
import router from './router';
import { createPinia } from 'pinia';
import { createHead } from '@unhead/vue/client';
import { TemplateParamsPlugin } from '@unhead/vue/plugins';
import { vuetify } from '@/plugins/vuetify';
import './styles/main.scss';
import { i18n } from '@/plugins/i18n';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { initDatadog } from '@/plugins/datadog';
import { initAxios } from '@/plugins/axios';
import { LicenseManager } from 'ag-grid-enterprise';
import appConfig, { type GmAppConfig } from '@/app.config';
import '/node_modules/@teamworksdev/vuetify/dist/src/styles/main.scss';
import { LDPlugin, LD_CLIENT } from 'launchdarkly-vue-client-sdk';
import { initRemotes } from './plugins/mfes';
import { initMaven } from './plugins/maven';
import { initCustomerIo } from '@/plugins/customerio';
import { setLDClient } from '@/plugins/launchdarkly';


initDatadog();

initAxios();

initRemotes();

initMaven();

initCustomerIo();

if (import.meta.env.VITE_AG_GRID_LICENSE_KEY) {
  LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);
}

const app = createApp(App);
const pinia = createPinia();

const head = createHead({
  plugins: [TemplateParamsPlugin],
});

const localFlags = import.meta.env.DEV && import.meta.env.VITE_LOCAL_FLAGS === 'true';

if (import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID) {
  // Context is intentionally omitted here: the SDK already defaults an unspecified
  // context to { anonymous: true, kind: 'user' }, which is exactly what we want prior
  // to session bootstrap identifying the client with a real user/org/team context.
  app.use(LDPlugin, {
    clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
    streaming: !localFlags,
    options: {
      sendEventsOnlyForVariation: true,
      ...(localFlags
        ? {
            bootstrap: {},
            streaming: false,
            sendEvents: false,
            diagnosticOptOut: true,
            fetchGoals: false,
            baseUrl: `${window.location.origin}/sandbox/flags`,
            streamUrl: `${window.location.origin}/sandbox/flags`,
            eventsUrl: `${window.location.origin}/sandbox/flags`,
          }
        : {}),
    },
  });

  // Borrow the single client instance the plugin just created (synchronously, since
  // deferInitialization is not set) so router guards and other plain modules can read
  // flags from it via getFlag(), and updateLDContext() can identify() it, without ever
  // creating a second client.
  setLDClient(app.runWithContext(() => inject(LD_CLIENT))!);
}

import { queryClient } from '@/plugins/vueQuery';

app
  .use(head)
  .use(VueQueryPlugin, { queryClient })
  .use(i18n)
  .use(pinia)
  .use(vuetify)
  .use(router)
  .provide<GmAppConfig>('appConfig', appConfig)
  .mount('#app');
